import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { submitFormData } from '../../../pages/api';

const Form_13 = () => {
    const navigate = useNavigate();

    const lineFields = [
        { label: 'Initial JB Weight - Left', name: 'initialJBWeight.left', type: 'number' },
        { label: 'Initial JB Weight - Middle', name: 'initialJBWeight.middle', type: 'number' },
        { label: 'Initial JB Weight - Right', name: 'initialJBWeight.right', type: 'number' },
        { label: 'JB with Glue Weight - Left', name: 'jbWithGlueWeight.left', type: 'number' },
        { label: 'JB with Glue Weight - Middle', name: 'jbWithGlueWeight.middle', type: 'number' },
        { label: 'JB with Glue Weight - Right', name: 'jbWithGlueWeight.right', type: 'number' },
        { label: 'Glue Weight - Left', name: 'glueWeight.left', type: 'number', readOnly: true },
        { label: 'Glue Weight - Middle', name: 'glueWeight.middle', type: 'number', readOnly: true },
        { label: 'Glue Weight - Right', name: 'glueWeight.right', type: 'number', readOnly: true },
        { label: 'Sealant Uniformity', name: 'sealantUniformity', type: 'text' },
        { label: 'Remark', name: 'remark', type: 'textarea' },
    ];

    const defaultLineData = lineFields.reduce((acc, field) => {
        const fieldName = field.name.split('.');
        if (fieldName.length > 1) {
            if (!acc[fieldName[0]]) acc[fieldName[0]] = {};
            acc[fieldName[0]][fieldName[1]] = '';
        } else {
            acc[field.name] = '';
        }
        return acc;
    }, {});

    const [lines, setLines] = useState([]);
    const [formData, setFormData] = useState({ date: '', shift: '', lines: {} });
    const [selectedLine, setSelectedLine] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Fetch lines from metadata and initialize form data
    useEffect(() => {
        const metadata = sessionStorage.getItem('metadata');
        if (metadata) {
            try {
                const parsedMetadata = JSON.parse(metadata);
                const fetchedLines = parsedMetadata.lines.map((item) => item.Lines);
                setLines(fetchedLines);
                setSelectedLine(fetchedLines[0] || '');

                const savedData = sessionStorage.getItem('JunctionBoxSealantWeightVerificationF13formData');
                let initialData = savedData ? JSON.parse(savedData) : { date: '', shift: '', lines: {} };

                // Initialize lines data if not present
                const defaultLines = fetchedLines.reduce(
                    (acc, line) => ({
                        ...acc,
                        [line]: { ...defaultLineData },
                    }),
                    {}
                );

                setFormData({
                    date: initialData.date || '',
                    shift: initialData.shift || '',
                    lines: {
                        ...defaultLines,
                        ...(initialData.lines || {}),
                    },
                });
            } catch (error) {
                console.error('Error parsing metadata or saved data:', error);
            }
        }
    }, []);

    // Prefill date, shift, and inspection data
    useEffect(() => {
        const inspectionDate = sessionStorage.getItem('inspectionDate');
        const inspectionShift = sessionStorage.getItem('inspectionShift');
        const inspectionData = sessionStorage.getItem('inspectionData');

        // Update date and shift
        if (inspectionDate && inspectionShift) {
            setFormData((prevData) => ({
                ...prevData,
                date: inspectionDate,
                shift: inspectionShift,
            }));
        }

        // Check if formData is empty for lines
        const isFormDataEmpty = Object.keys(formData.lines).every((line) =>
            Object.values(formData.lines[line]).every((value) => {
                if (typeof value === 'object') {
                    return Object.values(value).every((val) => val === '' || val === null || val === undefined);
                }
                return value === '' || value === null || value === undefined;
            })
        );

        // Prefill from inspectionData if formData is empty
        if (isFormDataEmpty && inspectionData && lines.length > 0) {
            try {
                const parsedInspectionData = JSON.parse(inspectionData);
                const verificationData = parsedInspectionData.JunctionBoxSealantWeightVerification;

                if (verificationData && verificationData.lines) {
                    setFormData((prevData) => {
                        const updatedLines = { ...prevData.lines };
                        lines.forEach((line) => {
                            if (verificationData.lines[line]) {
                                updatedLines[line] = {
                                    initialJBWeight: {
                                        left: verificationData.lines[line].initialJBWeight?.left || '',
                                        middle: verificationData.lines[line].initialJBWeight?.middle || '',
                                        right: verificationData.lines[line].initialJBWeight?.right || '',
                                    },
                                    jbWithGlueWeight: {
                                        left: verificationData.lines[line].jbWithGlueWeight?.left || '',
                                        middle: verificationData.lines[line].jbWithGlueWeight?.middle || '',
                                        right: verificationData.lines[line].jbWithGlueWeight?.right || '',
                                    },
                                    glueWeight: {
                                        left: verificationData.lines[line].glueWeight?.left || '',
                                        middle: verificationData.lines[line].glueWeight?.middle || '',
                                        right: verificationData.lines[line].glueWeight?.right || '',
                                    },
                                    sealantUniformity: verificationData.lines[line].sealantUniformity || '',
                                    remark: verificationData.lines[line].remark || '',
                                };
                            }
                        });

                        return {
                            ...prevData,
                            date: verificationData.date || prevData.date,
                            shift: verificationData.shift || prevData.shift,
                            lines: updatedLines,
                        };
                    });
                }
            } catch (error) {
                console.error('Error parsing inspectionData:', error);
            }
        }
    }, [lines]);

    // Function to calculate glue weights
    const calculateGlueWeights = (data, line) => {
        const initialJB = data.lines[line]?.initialJBWeight || {};
        const withGlue = data.lines[line]?.jbWithGlueWeight || {};

        const updatedLines = {
            ...data.lines,
            [line]: {
                ...data.lines[line],
                glueWeight: {
                    left: (parseFloat(withGlue.left) || 0) - (parseFloat(initialJB.left) || 0),
                    middle: (parseFloat(withGlue.middle) || 0) - (parseFloat(initialJB.middle) || 0),
                    right: (parseFloat(withGlue.right) || 0) - (parseFloat(initialJB.right) || 0),
                },
            },
        };

        return {
            ...data,
            lines: updatedLines,
        };
    };

    // Persist form data in sessionStorage
    useEffect(() => {
        if (Object.keys(formData.lines).length > 0) {
            sessionStorage.setItem('JunctionBoxSealantWeightVerificationF13formData', JSON.stringify(formData));
        }
    }, [formData]);

    const handleChange = (e, section = null, field = null) => {
        const { name, value } = e.target;

        setFormData((prevData) => {
            if (name === 'date' || name === 'shift') {
                return { ...prevData, [name]: value };
            }

            const updatedLines = { ...prevData.lines };
            updatedLines[selectedLine] = section
                ? {
                      ...updatedLines[selectedLine],
                      [section]: {
                          ...updatedLines[selectedLine][section],
                          [field]: value,
                      },
                  }
                : {
                      ...updatedLines[selectedLine],
                      [name]: value,
                  };

            let updatedData = { ...prevData, lines: updatedLines };
            // Recalculate glue weights after every change
            if (selectedLine) {
                updatedData = calculateGlueWeights(updatedData, selectedLine);
            }
            return updatedData;
        });
    };

    const handleLineChange = (e) => {
        setSelectedLine(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const payload = {
                projectId: localStorage.getItem('selectedProject'),
                inspectionSupplier: sessionStorage.getItem('inspectionSupplier'),
                inspectionDate: formData.date,
                inspectionShift: formData.shift,
                inspectorName: sessionStorage.getItem('username'),
                status: 'completed',
                JunctionBoxSealantWeightVerification: {
                    date: formData.date,
                    shift: formData.shift,
                    lines: formData.lines,
                },
            };

            await submitFormData(payload);
            alert('Form submitted successfully!');
        } catch (error) {
            console.error('Error submitting form:', error);
            alert(`Failed to submit form: ${error.message || 'Unknown error'}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-lg space-y-4">
            <h2 className="text-2xl font-bold text-center mb-4">Junction Box Sealant Weight Verification</h2>
            <p className="text-center font-semibold mb-4">Frequency: Once Per Shift</p>

            <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1">
                    <label className="block text-sm font-medium mb-1" htmlFor="date">
                        Date
                    </label>
                    <input
                        id="date"
                        type="date"
                        name="date"
                        value={formData.date}
                        readOnly
                        className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100"
                    />
                </div>
                <div className="flex-1">
                    <label className="block text-sm font-medium mb-1" htmlFor="shift">
                        Shift
                    </label>
                    <input
                        id="shift"
                        type="text"
                        name="shift"
                        value={formData.shift}
                        readOnly
                        className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100"
                    />
                </div>
                <div className="flex-1">
                    <label className="block text-sm font-medium mb-1" htmlFor="line">
                        Line
                    </label>
                    <select
                        id="line"
                        value={selectedLine}
                        onChange={handleLineChange}
                        className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={lines.length === 0}
                    >
                        {lines.length === 0 ? (
                            <option value="">No lines available</option>
                        ) : (
                            lines.map((line) => (
                                <option key={line} value={line}>
                                    {line}
                                </option>
                            ))
                        )}
                    </select>
                </div>
            </div>

            {lines.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {lineFields.map(({ label, name, type, readOnly }) => {
                        const fieldName = name.split('.');
                        return (
                            <div key={name}>
                                <label className="block text-sm font-medium mb-1" htmlFor={`${selectedLine}-${name}`}>
                                    {label}
                                </label>
                                {type === 'textarea' ? (
                                    <textarea
                                        id={`${selectedLine}-${name}`}
                                        name={name}
                                        value={
                                            fieldName.length > 1
                                                ? formData.lines[selectedLine]?.[fieldName[0]]?.[fieldName[1]] || ''
                                                : formData.lines[selectedLine]?.[name] || ''
                                        }
                                        onChange={(e) =>
                                            handleChange(
                                                e,
                                                fieldName.length > 1 ? fieldName[0] : null,
                                                fieldName.length > 1 ? fieldName[1] : null
                                            )
                                        }
                                        className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                ) : (
                                    <input
                                        id={`${selectedLine}-${name}`}
                                        type={type || 'text'}
                                        name={name}
                                        value={
                                            fieldName.length > 1
                                                ? formData.lines[selectedLine]?.[fieldName[0]]?.[fieldName[1]] || ''
                                                : formData.lines[selectedLine]?.[name] || ''
                                        }
                                        onChange={(e) =>
                                            !readOnly &&
                                            handleChange(
                                                e,
                                                fieldName.length > 1 ? fieldName[0] : null,
                                                fieldName.length > 1 ? fieldName[1] : null
                                            )
                                        }
                                        className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                            readOnly ? 'bg-gray-100' : ''
                                        }`}
                                        readOnly={readOnly}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="flex justify-between mt-6">
                <button
                    type="button"
                    onClick={() => navigate('/inspections/section2/form12')}
                    className="bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded hover:bg-gray-400 focus:outline-none"
                >
                    Previous
                </button>
                <button
                    type="submit"
                    disabled={isLoading}
                    className="bg-[#424752] text-white font-semibold py-2 px-4 rounded hover:bg-gray-500 focus:outline-none disabled:bg-gray-400"
                >
                    {isLoading ? 'Submitting...' : 'Submit'}
                </button>
                <button
                    type="button"
                    onClick={() => navigate('/inspections/section2/form14')}
                    className="bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded hover:bg-gray-400 focus:outline-none"
                >
                    Next
                </button>
            </div>
        </form>
    );
};

export default Form_13;